import { useEffect, useState } from 'react';
import { notify } from '../../helpers/toastify';
import MissionDetailCard from '../molecules/MissionDetailCard';
import { TitlePart, Modal } from '@bloomays-lib/ui.shared';
import { BrowserBloomer, GqlAccountManager, IContact, IMissionCardDetails, ISociety } from '@bloomays-lib/types.shared';
import { ApolloError } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { errorLogger } from '../../helpers/error';
import Stack from '@mui/material/Stack';
import MissionCard, { PopinType } from '../molecules/MissionCard';
import { AlertMessageActionType, DashboardAlertMessages } from '../molecules/DashboardAlertMessages';
import isEmpty from 'lodash/fp/isEmpty';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { TFunction } from 'i18next';
import { MissionPurchaseOrdersSection } from './MissionPurchaseOrdersSection';

export type ContextMissionDetailsProps = {
  error?: ApolloError;
  contextBloomer: BrowserBloomer;
  missionJobOwner: GqlAccountManager | null;
  contextMission: IMissionCardDetails;
  contextContactBloomer: IContact;
  contextContactBillingClient: IContact | IContact[];
  contextSocietyBloomer: ISociety;
  contextSocietyClient: ISociety;
  contextSocietyClientBilling: ISociety;
  contextContactOperationnal: IContact;
  contextLegalClientContact?: IContact;
  contextLegalClientSourceLabel?: string;
  contextLegalBloomerContact?: IContact;
  contextLegalBloomerSourceLabel?: string;
  legalContactUnavailableText?: string;
};

const getMissingFields = (
  t: TFunction,
  contextMission: IMissionCardDetails,
  setMissionPopinToOpen: (type: PopinType) => void,
): AlertMessageActionType[] => {
  const { clientSociety, pricing, contractType, bloomerBillingSociety, contactOperations, billingContacts } =
    contextMission;

  const slackUrl = `slack://channel?team=T010RDTFK9A&id=C02DDHHSU9F`;

  const missingFields: AlertMessageActionType[] = [];

  if (!clientSociety) {
    missingFields.push({
      title: t('clientSociety'),
      text: t('clientSocietyText'),
      type: 'Slack',
      onClick: () => {
        window.location.href = slackUrl;
      },
    });
  }

  if (!pricing) {
    missingFields.push({
      title: t('pricing'),
      text: t('pricingText'),
      type: 'Slack',
      onClick: () => {
        window.location.href = slackUrl;
      },
    });
  }

  if (!contractType) {
    missingFields.push({
      title: t('contractType'),
      text: t('contractTypeText'),
      type: 'Edit',
      onClick: () => {
        setMissionPopinToOpen('modify');
      },
    });
  }

  if (isEmpty(bloomerBillingSociety)) {
    missingFields.push({
      title: t('bloomerBillingSociety'),
      text: t('bloomerBillingSocietyText'),
      type: 'Slack',
      onClick: () => {
        window.location.href = slackUrl;
      },
    });
  } else if (bloomerBillingSociety && bloomerBillingSociety[0].portage) {
    if (!bloomerBillingSociety[0].billingContacts?.[0]) {
      missingFields.push({
        title: t('portageContact'),
        text: t('portageContactText'),
        type: 'Slack',
        onClick: () => {
          window.location.href = slackUrl;
        },
      });
    }
  }

  if (!contactOperations) {
    missingFields.push({
      title: t('contactOperations'),
      text: t('contactOperationsText'),
      type: 'Slack',
      onClick: () => {
        window.location.href = slackUrl;
      },
    });
  }

  if (isEmpty(billingContacts)) {
    missingFields.push({
      title: t('billingContacts'),
      text: t('billingContactsText'),
      type: 'Slack',
      onClick: () => {
        window.location.href = slackUrl;
      },
    });
  }
  return missingFields;
};

const ContextMissionDetails = ({
  error,
  contextBloomer,
  missionJobOwner,
  contextMission,
  contextContactBloomer,
  contextContactBillingClient,
  contextSocietyBloomer,
  contextSocietyClient,
  contextSocietyClientBilling,
  contextContactOperationnal,
  contextLegalClientContact,
  contextLegalClientSourceLabel,
  contextLegalBloomerContact,
  contextLegalBloomerSourceLabel,
  legalContactUnavailableText,
}: ContextMissionDetailsProps) => {
  const { t } = useTranslation(['dashboardMissingFields', 'mission']);

  const [openAlertModal, setOpenAlertModal] = useState<boolean>(false);
  const [missionPopinToOpen, setMissionPopinToOpen] = useState<PopinType>(null);
  const [alertFields, setAlertFields] = useState<AlertMessageActionType[]>([]);

  useEffect(() => {
    const missingFields = getMissingFields(t, contextMission, (type: PopinType) => setMissionPopinToOpen(type));
    if (!isEmpty(missingFields)) {
      setAlertFields(missingFields);
      setOpenAlertModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMission]);

  if (error) {
    errorLogger(error, {
      extraInfos: 'Internal server error, impossible to retrieve data about bloomers and their infos !',
    });
    notify('error', 'Impossible de récupérer la liste des bloomers. Erreur du server, essaie plus tard ! 😓', error);
  }

  return (
    <ContainerSuccess>
      <TitlePart textTitle={`${contextMission.label} - ${contextBloomer.fullname}`} />
      <Stack direction="row" spacing={2} justifyContent="center">
        {contextMission?.replacement && (
          <span
            id={`btnReplacement${contextMission?.recordId}`}
            style={{
              backgroundColor: '#faa3ba',
              padding: '10px 15px',
              borderRadius: '100px',
              fontSize: '14px',
            }}
          >
            Remplacement
          </span>
        )}
      </Stack>

      <MissionDetailsLayout>
        <Bloc1Container>
          <CardSlot>
            <MissionDetailCard
              type="bloomer"
              data={{
                bloomer: contextBloomer,
                missionOrigin: contextMission.origin,
                missionJobOwner,
              }}
            />
          </CardSlot>
          <CardSlot>
            <MissionCard
              mission={contextMission}
              popinToOpen={missionPopinToOpen}
              legalClientContact={contextLegalClientContact}
              legalClientSourceLabel={contextLegalClientSourceLabel}
              legalBloomerContact={contextLegalBloomerContact}
              legalBloomerSourceLabel={contextLegalBloomerSourceLabel}
              legalContactUnavailableText={legalContactUnavailableText}
              resetPopinToOpen={() => {
                setMissionPopinToOpen(null);
              }}
            />
          </CardSlot>
        </Bloc1Container>

        <Bloc2Container>
          <SectionTitle>{t('clientSocietyName', { ns: 'mission' })}</SectionTitle>
          <BlocVerticalStack>
            <CardSlot>
              <MissionDetailCard
                type="society"
                data={{ society: contextSocietyClient, extraInfoTitle: 'du client', mission: contextMission }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="society"
                data={{
                  society: contextSocietyClientBilling,
                  extraInfoTitle: 'de facturation du client',
                  mission: contextMission,
                }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="contact"
                data={{ contact: contextContactBillingClient, extraInfoTitle: 'client de facturation' }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="contact"
                data={{ contact: contextContactOperationnal, extraInfoTitle: 'client opérationnel' }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="contact"
                data={{
                  contact: contextLegalClientContact,
                  extraInfoTitle: 'legal client',
                  sourceTagText: contextLegalClientSourceLabel,
                  unavailableText: legalContactUnavailableText,
                }}
              />
            </CardSlot>
          </BlocVerticalStack>
        </Bloc2Container>

        <Bloc3Container>
          <SectionTitle>{t('bloomerSocietyName', { ns: 'mission' })}</SectionTitle>
          <BlocVerticalStack>
            <CardSlot>
              <MissionDetailCard
                type="society"
                data={{ society: contextSocietyBloomer, extraInfoTitle: 'du bloomer', mission: contextMission }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="contact"
                data={{ contact: contextContactBloomer, extraInfoTitle: 'bloomer de facturation' }}
              />
            </CardSlot>
            <CardSlot>
              <MissionDetailCard
                type="contact"
                data={{
                  contact: contextLegalBloomerContact,
                  extraInfoTitle: 'legal bloomer',
                  sourceTagText: contextLegalBloomerSourceLabel,
                  unavailableText: legalContactUnavailableText,
                }}
              />
            </CardSlot>
          </BlocVerticalStack>
        </Bloc3Container>

        <Bloc4Container>
          <MissionPurchaseOrdersSection mission={contextMission} clientBillingSociety={contextSocietyClientBilling} />
        </Bloc4Container>
      </MissionDetailsLayout>
      <Modal
        additionnalCSS={{ borderRadius: '5px' }}
        open={openAlertModal === true}
        onClose={() => {
          setOpenAlertModal(false);
        }}
      >
        <DashboardAlertMessages fields={alertFields} />
      </Modal>
    </ContainerSuccess>
  );
};

export default ContextMissionDetails;

const ContainerSuccess = styled('div')(() => ({
  backgroundColor: 'rgb(255, 255, 255)',
  width: '100%',
  margin: 'auto',
}));

const MissionDetailsLayout = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 24,
  alignItems: 'start',
  padding: '0 8px',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const Bloc1Container = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 16,
  width: '100%',

  [theme.breakpoints.up('md')]: {
    gridColumn: '1 / -1',
    gridTemplateColumns: '1fr 1fr',
  },
}));

const Bloc2Container = styled('div')(() => ({
  width: '100%',
}));

const Bloc3Container = styled('div')(() => ({
  width: '100%',
}));

const Bloc4Container = styled('div')(({ theme }) => ({
  width: '100%',

  [theme.breakpoints.up('md')]: {
    gridColumn: '1 / -1',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  margin: '0 0 12px 0',
  color: theme.palette.neutral[80],
  fontWeight: 700,
}));

const BlocVerticalStack = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  alignItems: 'center',
}));

const CardSlot = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: 400,
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    minHeight: 420,
  },
}));
